/**
 * @fileoverview Main Form component for the Storyteller application.
 * Handles form rendering, validation, and submission.
 *
 * @example
 * // Basic usage in a component:
 * import { Form } from './Form'
 *
 * <Form {...props} />
 */

import {
    ErrorTranslator,
    JsonSchema,
    Translator,
    UISchemaElement,
    createAjv,
} from '@jsonforms/core'
import { JsonForms } from '@jsonforms/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorObject } from 'ajv'
import { NavigationButton } from '../../NavigationButton'
import { renderers } from './renderers'

import { JSONFormContext } from '../../../JSONFormContext'
import { PermissionManager } from '../../../PermissionManager'
import { ExitButton } from '../../ExitButton'
import css from './Form.module.css'

type ValidationOptions = 'ValidateAndShow' | 'ValidateAndHide'

/**
 * @function Form
 * @description Main Form component for the Storyteller application.
 * Handles form rendering, validation, and submission.
 * @param {Novoic.FormQuestionProps} props - The props for the Form component.
 * @returns {JSX.Element} The Form component.
 */
export function Form(props: Novoic.FormQuestionProps) {
    const { errors: validationErrors, onSubmit } = props
    const { id, question } = props.question
    const {
        data: initialData = {},
        schema,
        uischema,
        errorMessages,
        button,
        buttonType = 'confirm',
        permissions,
        skip,
        exit,
    } = question

    const [data, setData] = useState<Record<string, unknown>>(initialData)
    const [errors, setErrors] = useState<ErrorObject[]>([])
    const [additionalErrors, setAdditionalErrors] = useState<ErrorObject[]>([])

    const hasRequiredFields = schema?.required?.length > 0
    const hasInput = Object.keys(data).filter((key) => data[key] !== undefined).length > 0
    const hasSchema = schema !== undefined
    const hasButton = button !== undefined
    const isOptional = hasSchema && !hasInput && !hasRequiredFields
    const canSkip = skip !== undefined
    const canExit = exit !== undefined

    useEffect(() => {
        if (validationErrors) {
            setAdditionalErrors(
                Object.entries(validationErrors).map(([key, value]: [string, string]) => ({
                    instancePath: `/${key}`,
                    message: value,
                    schemaPath: '',
                    schema: [key],
                    keyword: value,
                    params: {},
                }))
            )
        }
    }, [validationErrors])

    const [validationMode, setValidationMode] = useState<ValidationOptions>('ValidateAndHide')
    const { t } = useTranslation()

    const locale = 'en'
    const ajv = createAjv({ useDefaults: true })
    ajv.addKeyword({
        keyword: 'composite',
        type: 'object',
        validate: (_, data: Record<string, unknown>, { schema }: { schema: JsonSchema }) => {
            const requiredFields = Object.keys(schema.properties).filter(
                (key) => schema.properties[key].required
            )

            let isValid = true

            requiredFields.forEach((key) => {
                switch (schema.properties[key].type) {
                    case 'string':
                        isValid = isValid && data[key] !== undefined && data[key] !== ''
                        break
                    case 'number':
                        isValid = isValid && data[key] !== undefined && data[key] !== 0
                        break
                    case 'boolean':
                        isValid = isValid && data[key] !== undefined
                        break
                    default:
                        isValid = isValid && data[key] !== undefined
                }
            })

            return isValid
        },
    })

    const translateError: ErrorTranslator = (
        error: ErrorObject,
        _: Translator,
        uischema: UISchemaElement
    ) => {
        const keyword = error.keyword
        const schema = uischema.options.name
        const override = uischema.options?.translationOverride?.errors

        return (
            (override && t(override[keyword])) ||
            errorMessages?.[keyword] ||
            t(`errors.${schema}.error.${keyword}`)
        )
    }

    async function onConfirm() {
        if (errors.length > 0) {
            setValidationMode('ValidateAndShow')
            return
        } else if (permissions?.length > 0) {
            const isGranted = await PermissionManager.request(permissions)
            return onSubmit(id, {
                ...data,
                permissions: isGranted,
            })
        } else {
            setValidationMode('ValidateAndHide')
            setErrors([])
            setAdditionalErrors([])
        }

        return onSubmit(id, data)
    }

    function getButton(hasButton: boolean, isOptional: boolean) {
        if (!hasButton) {
            return null
        }

        if (isOptional) {
            return (
                <NavigationButton
                    type="cancel"
                    label="buttons.skip"
                    onClick={() => onSubmit(id, {})}
                />
            )
        }

        return <NavigationButton type={buttonType} label={button} onClick={onConfirm} />
    }

    return (
        <JSONFormContext.Provider
            value={{
                clearErrors: () => {
                    setValidationMode('ValidateAndHide')
                    setErrors([])
                    setAdditionalErrors([])
                },
                onSubmit: (data: Record<string, unknown>) => onSubmit(id, data),
            }}
        >
            <div className={css.container}>
                <JsonForms
                    schema={schema}
                    uischema={uischema}
                    data={data}
                    i18n={{ locale: locale, translateError }}
                    renderers={renderers}
                    additionalErrors={additionalErrors}
                    validationMode={validationMode}
                    onChange={({ data, errors }) => {
                        setData(data)
                        setErrors(errors)
                        setValidationMode('ValidateAndHide')
                    }}
                    ajv={ajv}
                />
                {(canExit || canSkip || isOptional || hasButton) && (
                    <div className={css.button_container}>
                        {canSkip && (
                            <NavigationButton
                                type="cancel"
                                label={skip}
                                onClick={() => onSubmit(id, { skip: true })}
                            />
                        )}
                        {canExit && (
                            <ExitButton
                                id={id}
                                confirmationNeeded={uischema.options?.exitConfirmationNeeded}
                                onSubmit={onSubmit}
                                label={exit}
                            />
                        )}
                        {getButton(hasButton, isOptional)}
                    </div>
                )}
            </div>
        </JSONFormContext.Provider>
    )
}
